/**
 * Announcement messages are plain text with inline links written as `[text](url)`; bare
 * `http(s)://` URLs are links too. Only http(s) URLs ever become links, so a stored
 * `[x](javascript:...)` renders as text and never runs.
 */
export type AnnouncementMessageSegment = { type: 'text'; text: string } | { type: 'link'; text: string; url: string };

const LINK_PATTERN = /\[([^\]\n]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s<>]+)/g;
/** Punctuation that usually ends a sentence rather than the URL it follows. */
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

export function isSafeAnnouncementLinkUrl(url: string): boolean {
    try {
        const { protocol } = new URL(url);
        return protocol === 'http:' || protocol === 'https:';
    } catch {
        return false;
    }
}

export function parseAnnouncementMessage(message: string): AnnouncementMessageSegment[] {
    const segments: AnnouncementMessageSegment[] = [];
    const pushText = (text: string) => {
        if (!text) return;
        const last = segments[segments.length - 1];
        if (last?.type === 'text') last.text += text;
        else segments.push({ type: 'text', text });
    };

    let cursor = 0;
    for (const match of (message ?? '').matchAll(LINK_PATTERN)) {
        const [whole, label, markdownUrl, bareUrl] = match;
        const start = match.index ?? 0;
        pushText(message.slice(cursor, start));

        if (label !== undefined) {
            // An unsafe target keeps its label as plain text.
            if (isSafeAnnouncementLinkUrl(markdownUrl)) segments.push({ type: 'link', text: label, url: markdownUrl });
            else pushText(label);
        } else {
            const trailing = bareUrl.match(TRAILING_PUNCTUATION)?.[0] ?? '';
            const url = bareUrl.slice(0, bareUrl.length - trailing.length);
            if (isSafeAnnouncementLinkUrl(url)) segments.push({ type: 'link', text: url, url });
            else pushText(url);
            pushText(trailing);
        }
        cursor = start + whole.length;
    }
    pushText((message ?? '').slice(cursor));

    return segments;
}

/** Targets of `[text](url)` links that would not render as links (anything but http/https). */
export function getUnsafeAnnouncementLinks(message: string): string[] {
    return Array.from((message ?? '').matchAll(LINK_PATTERN))
        .filter(([, label, url]) => label !== undefined && !isSafeAnnouncementLinkUrl(url))
        .map(([, , url]) => url);
}

/** The message as a reader sees it, with links reduced to their text. For one-line previews. */
export function announcementMessageToPlainText(message: string): string {
    return parseAnnouncementMessage(message)
        .map((segment) => segment.text)
        .join('');
}
